/**
 * ThemeSync — Sincroniza el tema light/dark/system entre dispositivos.
 * iter.248 · Fase 10 UX Refactor — cross-device consistency.
 *
 * Comportamiento:
 *  - Al montar: si el usuario logueado tiene `preferences.theme` en el server,
 *    aplícalo via `setTheme()` (sobrescribe el localStorage local).
 *  - Cuando el usuario cambia el tema (via `ThemeToggle`), PATCH al server
 *    (fire-and-forget, silencioso si falla — localStorage sigue sirviendo).
 *
 * Este componente NO renderiza nada. Debe ir dentro de `<ThemeProvider>`.
 */
import { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import api from "../../lib/api";

export function ThemeSync() {
  const { theme, setTheme } = useTheme();
  const hydrated = useRef(false);
  const lastPersisted = useRef(null);

  // 1) On mount: pull server preference and apply.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.getMe();
        const serverTheme = me?.preferences?.theme;
        if (!cancelled && serverTheme && ["light", "dark", "system"].includes(serverTheme)) {
          if (serverTheme !== theme) {
            setTheme(serverTheme);
          }
          lastPersisted.current = serverTheme;
        }
      } catch {
        // silent — falls back to localStorage default
      } finally {
        if (!cancelled) hydrated.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2) On theme change (after hydrate): persist to server.
  useEffect(() => {
    if (!hydrated.current) return;
    if (!theme) return;
    if (theme === lastPersisted.current) return;
    if (!["light", "dark", "system"].includes(theme)) return;
    lastPersisted.current = theme;
    api.request("/auth/me/preferences", {
      method: "PATCH",
      body: JSON.stringify({ theme }),
    }).catch(() => { /* silent — localStorage remains fallback */ });
  }, [theme]);

  return null;
}
